import { CheckIcon, FileTextIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import useDatasources from '@/hooks/useDatasources';
import useDocuments from '@/hooks/useDocuments';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { cn } from '@/lib/utils';
import { documentLink } from '@/modules/collections/utils/documentLink';
import { datasourceIconByType } from '@/modules/tryIt/components/Messages/MessageNode/MessageNodeHeader';
import { selectAccountSlug } from '@/redux/session/selectors';

import { OutcomeBadge } from './StepRows';

type DocumentLineProps = {
  collectionId: string;
  datasourceId: string;
  documentId: string;
  name?: string;
  score?: number;
  scoreLabel?: string;
  isSelected?: boolean;
};

const Row = ({
  icon,
  children,
  trailing,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) => (
  <li className="flex min-h-5 items-center justify-between gap-2 self-stretch text-sm leading-5 text-muted-foreground">
    <div className="flex min-w-0 items-center justify-center gap-1">
      <span className="flex size-4 shrink-0 items-center justify-center">{icon}</span>
      {children}
    </div>
    {trailing}
  </li>
);

const StatusPill = ({ isDraft }: { isDraft: boolean }) => {
  const { t } = useTranslation();

  return (
    <InfoTooltip
      className="z-3000"
      content={
        isDraft
          ? t('collections.status_tooltip.draft_testing_context')
          : t('collections.status_tooltip.published')
      }
      side="top"
    >
      <OutcomeBadge flavour={isDraft ? 'neutral' : 'successful'}>
        {isDraft ? t('collections.is_draft') : t('collections.published')}
      </OutcomeBadge>
    </InfoTooltip>
  );
};

const DocumentName = ({ name, to }: { name: string; to: string }) => {
  const { t } = useTranslation();
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>('horizontal', [name]);

  return (
    <InfoTooltip
      className="z-3000"
      size={isTruncated ? 'large' : 'default'}
      content={isTruncated ? name : t('try_it.details.go_to_document')}
    >
      <span ref={ref} className="min-w-0 truncate">
        <NavLink
          className="hover:text-informational cursor-pointer text-sm leading-none font-medium text-foreground hover:underline"
          target="_blank"
          rel="noopener noreferrer"
          to={to}
        >
          {name}
        </NavLink>
      </span>
    </InfoTooltip>
  );
};

const SelectedCheck = () => {
  const { t } = useTranslation();

  return (
    <CheckIcon
      role="img"
      aria-label={t('try_it.details.source_selected')}
      className="size-4 shrink-0 text-success"
    />
  );
};

const Outcome = ({
  isDraft,
  score,
  scoreLabel,
}: {
  isDraft: boolean;
  score?: number;
  scoreLabel?: string;
}) => {
  const metric = scoreLabel ?? score?.toFixed(2);

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <StatusPill isDraft={isDraft} />
      {metric !== undefined && (
        <>
          <span className="text-xs leading-5 text-muted-foreground">·</span>
          <span
            className={cn(
              'shrink-0 text-right text-xs leading-4 font-normal text-muted-foreground',
              scoreLabel ? 'min-w-20' : 'w-8 font-mono'
            )}
          >
            {metric}
          </span>
        </>
      )}
    </div>
  );
};

export const useDocumentLink = (collectionId: string, datasourceId: string, documentId: string) => {
  const slug = useSelector(selectAccountSlug);
  const { datasources } = useDatasources(collectionId);
  const datasource = datasources?.find((entry) => entry.datasource_id === datasourceId);

  if (!slug || !datasource) {
    return null;
  }

  return documentLink(slug, collectionId, datasourceId, documentId, datasource);
};

export const DocumentLine = ({
  collectionId,
  datasourceId,
  documentId,
  name,
  score,
  scoreLabel,
  isSelected = false,
}: DocumentLineProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);

  const { datasources, listStatus } = useDatasources(collectionId);
  const datasource = datasources?.find((entry) => entry.datasource_id === datasourceId);
  const { document, getStatus: documentStatus } = useDocuments(
    collectionId,
    datasource?.datasource_id,
    documentId
  );

  if (!collectionId || !documentId) {
    return null;
  }

  if (!datasourceId) {
    return (
      <Row icon={<FileTextIcon size={16} />}>
        <span className="min-w-0 truncate text-sm leading-none font-medium text-foreground">
          {name ?? t('activityLogs.document_deleted')}
        </span>
        {isSelected && <SelectedCheck />}
      </Row>
    );
  }

  if (listStatus === 'pending') {
    return null;
  }

  if (!datasource) {
    return (
      <Row icon={<FileTextIcon size={16} />}>
        <span className="min-w-0 flex-1">{t('activityLogs.document_deleted')}</span>
      </Row>
    );
  }

  const icon = datasourceIconByType[datasource.type];

  if (documentStatus === 'pending') {
    return null;
  }

  if (!document) {
    return (
      <Row icon={icon}>
        <span className="min-w-0 flex-1 truncate">
          {t('activityLogs.deleted_from')}{' '}
          <InfoTooltip className="z-3000" content={t('try_it.details.go_to_datasource')}>
            <NavLink
              className="cursor-pointer text-primary hover:text-primary"
              target="_blank"
              rel="noopener noreferrer"
              to={`/${slug}/knowledge-bases/${collectionId}/datasources/${datasourceId}`}
            >
              {datasource.name}
            </NavLink>
          </InfoTooltip>
        </span>
      </Row>
    );
  }

  return (
    <Row
      icon={icon}
      trailing={<Outcome isDraft={document.is_draft} score={score} scoreLabel={scoreLabel} />}
    >
      <DocumentName
        name={document.name}
        to={documentLink(slug, collectionId, datasourceId, documentId, datasource)}
      />
      {isSelected && <SelectedCheck />}
    </Row>
  );
};
